/**
 * @file Public landing page — pages/Landing/LegalPage.tsx.
 */

import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import "./LandingPage.css";
import PublicPageContent from "./components/PublicPageContent";
import type { PublicPageSection } from "../../services/publicPageService";
import "./components/PublicStaticPage.css";

const LegalPage: React.FC = () => {
  const [pageTitle, setPageTitle] = useState("Legal");
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);

  const fallbackSections: PublicPageSection[] = useMemo(
    () => [
      {
        body: "All content on icomec.in is for informational purposes only.",
      },
      {
        heading: "No Liability",
        body: "We strive for accuracy but make no guarantees regarding the completeness or reliability of the information provided.",
      },
      {
        heading: "Copyright",
        body: "Unless otherwise stated, all text, images, and branding are the intellectual property of ICoMEC.",
      },
    ],
    []
  );

  return (
    <div className="landing-root">
      <header className="landing-header flex justify-center items-center space-x-3 text-center py-4">
        <h1 className="landing-main-title text-4xl font-bold text-primary">
          ICoMEC
        </h1>
        <h2 className="landing-sub-title text-xl text-grayDark">
          Indian Construction Materials Embodied Carbon Data Base
        </h2>
      </header>

      <nav className="landing-nav">
        <Link to="/">Home</Link>
        <Link to="/join">Join</Link>
        <Link to="/impact">Impact</Link>
        <Link to="/research">Research and Analysis</Link>
        <Link to="/about">About</Link>
      </nav>

      <section className="public-static-section">
        <div className="public-static-card">
          <div className="public-static-header">
            <div>
              <h1 className="public-static-title">{pageTitle}</h1>
              <p className="public-static-subtitle">
                Legal information and notices for this website.
              </p>
            </div>
            {updatedAt && (
              <div className="public-static-meta">
                Last updated: {new Date(updatedAt).toLocaleDateString()}
              </div>
            )}
          </div>
          <hr className="public-static-divider" />

          <PublicPageContent
            slug="legal"
            fallbackSections={fallbackSections}
            onMeta={({ title, updatedAt }) => {
              if (title) setPageTitle(title);
              if (updatedAt) setUpdatedAt(updatedAt);
            }}
          />
        </div>
      </section>

      <footer className="landing-footer">
        <div className="footer-main">
          <div className="footer-left">
            <h2>ICoMEC</h2>
            <p>
              Indian Construction Materials Embodied
              <br /> Carbon Data Base
            </p>
          </div>

          <div className="footer-links">
            <div>
              <h4>About Us</h4>
              <ul>
                <li><Link to="/who-we-are">Who We Are</Link></li>
                <li><Link to="/what-we-do">What We Do</Link></li>
                <li><Link to="/leadership">Leadership</Link></li>
                <li><Link to="/partners">Partners</Link></li>
                <li><Link to="/faq">FAQ</Link></li>
                <li><Link to="/glossary">Glossary</Link></li>
              </ul>
            </div>
            <div>
              <h4>Resources</h4>
              <ul>
                {/* <li><Link to="/resource-library">Resource Library</Link></li> */}
                <li><Link to="/events">Events</Link></li>
              </ul>
            </div>
          </div>
        </div>
      </footer>

      <div className="footer-bottom">
        <p>
          <Link to="/cookies-policy">Cookies Policy</Link> · <Link to="/privacy-policy">Privacy Policy</Link> · <Link to="/legal">Legal</Link> · <Link to="/terms-of-use">Terms of Use</Link>
          {/* · <Link to="/imprint">Imprint</Link> */}
        </p>
        <p>© 2026 CARBSE. All rights reserved.</p>
      </div>
    </div>
  );
};

/** React component — Legal Page. */
export default LegalPage;

